import { useMemo, useCallback } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Table, Button } from 'antd'
import { FileExcelOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import PropTypes from 'prop-types'
import { pick } from 'lodash'
import queryString from 'query-string'
import Filters from '../Forms/Filters'
import usePracticeCycles from '../Common/usePracticeCycles'
import { ListError } from '../Common'
import { PAGE_SIZE_OPTIONS, PAGE_SIZE } from '../../lib/constants'
import { toPath } from '../../routes'
import { useAsync } from '../../hooks'
import useExportExcel from '../../hooks/useExportExcel'
import useFilters from '../Cycles/useFilters'
import {
  useFetchMissingOrders,
  useMissingOrders,
} from '../../redux/slices/missing-orders'
import ShowTotal from '../Common/ShowTotal'

dayjs.extend(utc)

const DEFAULT_FILTERS = {
  missingPage: 1,
  missingPageSize: PAGE_SIZE,
  missingQuery: '',
  duplicates: 'false',
}

const MISSING_KEYS = [
  'cycleId',
  'missingQuery',
  'missingPage',
  'missingPageSize',
]

const useStyles = createUseStyles({
  present: {
    color: 'green',
  },
  notPresent: {
    color: 'orange',
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
  },
  exportContainer: {
    textAlign: 'center',
  },
})

export default function MissingOrders({ cycleId, activeTab }) {
  const classes = useStyles()

  const columns = [
    {
      title: 'Patient MRN',
      align: 'right',
      dataIndex: 'patient.practiceMrn',
    },
    {
      title: 'Patient Name',
      render: (_, { patient }) => (
        <Link to={toPath.patients.patient({ id: patient.id })}>
          {patient.lastName}, {patient.firstName}
        </Link>
      ),
    },
    {
      title: 'CPT',
      dataIndex: 'cptCode',
    },
    {
      title: 'ICD',
      dataIndex: 'icd10Code',
    },
    {
      title: 'Tech DOS',
      dataIndex: 'billingDate',
      render: (_, { tos }) => <div>{dayjs(tos).format('MMM D, YYYY')}</div>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, record) => {
        const status = record.status || '--'
        return <div>{status}</div>
      },
    },
  ]

  // eslint-disable-next-line no-unused-vars
  const [_, { practice, practiceId }] = usePracticeCycles()

  const { conditions, setConditions } = useFilters(DEFAULT_FILTERS)

  const params = useMemo(
    () => ({
      ...pick(conditions, MISSING_KEYS),
    }),
    [conditions],
  )


  const setPage = useCallback(
    newPage =>
      setConditions({
        cycleId,
        activeTab,
        ...params,
        missingPage: newPage,
      }),
    [cycleId, activeTab, params, setConditions],
  )

  const setPageSize = useCallback(
    (newPage, newPageSize) =>
      setConditions({
        cycleId,
        activeTab,
        ...conditions,
        missingPage: newPage,
        missingPageSize: newPageSize,
      }),
    [activeTab, conditions, cycleId, setConditions],
  )

  const setFilters = useCallback(
    newConditions =>
      setConditions({
        ...conditions,
        cycleId,
        activeTab,
        missingQuery: newConditions.query,
        missingPage: 1,
        page: 1,
      }),
    [activeTab, conditions, cycleId, setConditions],
  )

  const { error, clearError, pending } = useAsync(
    useFetchMissingOrders(conditions, true),
  )

  const { missingOrders, total } = useMissingOrders(params)
  const { missingPage, missingPageSize } = {
    ...conditions,
  }

  const excelExportUrl = useMemo(
    () =>
      queryString.stringifyUrl({
        url: `/reports/orders/${cycleId}/missing-orders/`,
        query: {
          q: conditions.missingQuery,
        },
      }),
    [cycleId, conditions.missingQuery],
  )

  const [exportingExcel, exportExcel] = useExportExcel(excelExportUrl)

  return (
    <div>
      <Filters
        defaultValues={DEFAULT_FILTERS}
        currentValues={{ ...params, query: params.missingQuery }}
        onSubmit={setFilters}
        placeholders={{ query: 'Search Authorization or Patient' }}
        showSearch
      />
      {error && <ListError description={error.message} onClose={clearError} />}
      <Table
        rowKey="id"
        columns={columns}
        className={classes.ordersTable}
        dataSource={missingOrders}
        loading={pending}
        pagination={{
          total,
          page: +missingPage,
          pageSize: +missingPageSize,
          onChange: setPage,
          onShowSizeChange: setPageSize,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: <ShowTotal orders={missingOrders} total={total} />,
        }}
      />
      {Boolean(missingOrders.length) && (
        <div className={classes.exportContainer}>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}

MissingOrders.propTypes = {
  cycleId: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
}
